import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, ChevronLeft, ChevronRight } from 'lucide-react';
import { SectionHeading } from './Shared';
const testimonials = [{
  quote: "The biggest difference is time. Before, I spent two evenings a week preparing sessions. Now I open the pack, adapt the session to my team and in a few minutes I'm ready to go.",
  author: 'Coach David R.',
  role: 'U16 Volleyball Coach',
  avatar: 'https://randomuser.me/api/portraits/men/32.jpg'
}, {
  quote: 'I finally feel like a professional coach. My players can see the difference in how organized and confident I am on the court.',
  author: 'Coach Sarah M.',
  role: 'Club Volleyball Coach',
  avatar: 'https://randomuser.me/api/portraits/women/44.jpg'
}, {
  quote: 'I used to repeat the same 5 drills every week because I had nothing else ready. Now I have 40+ drills at my fingertips and practice is completely different.',
  author: 'Coach James T.',
  role: 'School Volleyball Program',
  avatar: 'https://randomuser.me/api/portraits/men/67.jpg'
}];
export const Testimonials = () => {
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const paginate = useCallback((newDirection: number) => {
    setDirection(newDirection);
    setIndex(prev => (prev + newDirection + testimonials.length) % testimonials.length);
  }, []);
  const goTo = (i: number) => {
    setDirection(i > index ? 1 : -1);
    setIndex(i);
  };
  // Auto-advance every 6 seconds
  useEffect(() => {
    const timer = setInterval(() => paginate(1), 6000);
    return () => clearInterval(timer);
  }, [paginate, index]);
  const t = testimonials[index];
  const variants = {
    enter: (dir: number) => ({
      opacity: 0,
      x: dir > 0 ? 40 : -40
    }),
    center: {
      opacity: 1,
      x: 0
    },
    exit: (dir: number) => ({
      opacity: 0,
      x: dir > 0 ? -40 : 40
    })
  };
  return <section className="py-12 md:py-24 bg-slate-50 text-navy-900" data-id="element-193" data-mp="1xh7pwzwc4it6">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-194" data-mp="1sinywl2vwgty">
        <SectionHeading data-id="element-195" data-mp="1ohatud7m8ho3">What Coaches Are Already Saying:</SectionHeading>

        <div className="relative" data-id="element-196" data-mp="1ofhqb6saudkb">
          <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6 md:p-10 min-h-[280px] flex items-center" data-id="element-197" data-mp="1jihqhdm1m71n">
            <AnimatePresence mode="wait" custom={direction} data-id="element-198" data-mp="1ek6h13zntyjy">
              <motion.div key={index} custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.4,
              ease: 'easeInOut'
            }} className="flex flex-col items-center text-center w-full" data-id="element-199" data-mp="19semyjhapap2">
                <img src={t.avatar} alt={`Portrait of ${t.author}`} className="w-16 h-16 md:w-20 md:h-20 rounded-full object-cover border-4 border-vbgold/30 shadow-md mb-4" data-id="element-200" data-mp="qtajdq8iw94s" />
                <div className="flex gap-1 mb-4" data-id="element-201" data-mp="37i3feobojtnu">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 md:w-5 md:h-5 fill-vbgold text-vbgold" data-id="element-202" data-mp="3ccz53htg24xs" data-mpi={i} />)}
                </div>
                <p className="text-slate-700 italic text-base md:text-lg mb-6 leading-relaxed" data-id="element-203" data-mp="2jobj1k0vfbj6">
                  "{t.quote}"
                </p>
                <div data-id="element-204" data-mp="2squgtppsuxyk">
                  <p className="font-bold text-navy-900" data-id="element-205" data-mp="2sqb0uejpmjn6">{t.author}</p>
                  <p className="text-xs md:text-sm text-slate-500 uppercase tracking-wider mt-1" data-id="element-206" data-mp="2xicfu889iogw">
                    {t.role}
                  </p>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Navigation Arrows */}
          <button onClick={() => paginate(-1)} aria-label="Previous testimonial" className="absolute top-1/2 -translate-y-1/2 -left-2 md:-left-5 bg-white border border-slate-200 rounded-full p-2 shadow-md hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-vbred transition-colors" data-id="element-207" data-mp="1lwwqm42e76mq">
            <ChevronLeft className="w-5 h-5 md:w-6 md:h-6 text-navy-900" data-id="element-208" data-mp="1quygb7ubq476" />
          </button>
          <button onClick={() => paginate(1)} aria-label="Next testimonial" className="absolute top-1/2 -translate-y-1/2 -right-2 md:-right-5 bg-white border border-slate-200 rounded-full p-2 shadow-md hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-vbred transition-colors" data-id="element-209" data-mp="24md3lqv9krk1">
            <ChevronRight className="w-5 h-5 md:w-6 md:h-6 text-navy-900" data-id="element-210" data-mp="29kel47d1kite" />
          </button>
        </div>

        {/* Dots */}
        <div className="flex justify-center gap-2 mt-6" data-id="element-211" data-mp="f8q2nv7lpy49">
          {testimonials.map((_, i) => <button key={i} onClick={() => goTo(i)} aria-label={`Go to testimonial ${i + 1}`} className={`h-2 rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-vbred ${i === index ? 'w-6 bg-vbred' : 'w-2 bg-slate-300 hover:bg-slate-400'}`} data-id="element-212" data-mp="2qw1yl003m07s" data-mpi={i} />)}
        </div>
      </div>
    </section>;
};